import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Send, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

const NAME_KEY = "bothra_username";

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "adesso";
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    return `${Math.floor(diff / 86400)} g fa`;
  } catch {
    return "";
  }
}

const COLORS = ["bg-sky-500", "bg-orange-500", "bg-emerald-500", "bg-violet-500", "bg-rose-500", "bg-cyan-500", "bg-amber-500"];
function avatarColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return COLORS[Math.abs(h) % COLORS.length];
}

export function CommunityBoard({ spots = [], activeSpotId }) {
  const [posts, setPosts] = useState([]);
  const [author, setAuthor] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [spotId, setSpotId] = useState(activeSpotId || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const { data } = await api.get("/community");
    setPosts(data);
  };
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (activeSpotId && !spotId) setSpotId(activeSpotId);
  }, [activeSpotId]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!author.trim() || !message.trim()) {
      toast.error("Inserisci nome utente e messaggio");
      return;
    }
    setSending(true);
    const spot = spots.find((s) => s.id === spotId);
    try {
      await api.post("/community", {
        author: author.trim(),
        spot_id: spotId || null,
        spot_name: spot?.name || "",
        message: message.trim(),
      });
      localStorage.setItem(NAME_KEY, author.trim());
      setMessage("");
      toast.success("Pubblicato sulla bacheca Bothra Boys!");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella pubblicazione");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="community-board">
      <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-4" data-testid="community-form">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-xl bg-orange-100 flex items-center justify-center">
            <Users className="h-4 w-4 text-orange-600" />
          </div>
          <h3 className="font-head text-lg font-bold text-slate-900">Scrivi sulla bacheca</h3>
          <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-slate-400">senza registrarti</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="c-author">Nome utente</Label>
            <Input id="c-author" value={author} onChange={(e) => setAuthor(e.target.value)} maxLength={40}
              className="mt-1.5 rounded-xl h-11" placeholder="Es. Rider99" data-testid="community-author" />
          </div>
          <div>
            <Label>Spot</Label>
            <Select value={spotId} onValueChange={setSpotId}>
              <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="community-spot-select">
                <SelectValue placeholder="Quale spot?" />
              </SelectTrigger>
              <SelectContent>
                {spots.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="c-msg">Messaggio</Label>
          <Textarea id="c-msg" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={800} rows={3}
            className="mt-1.5 rounded-xl" placeholder="Com'è la situazione? Condividi con i Bothra Boys..." data-testid="community-message" />
        </div>

        <Button type="submit" disabled={sending} className="w-full h-11 rounded-xl bg-orange-500 hover:bg-orange-600 font-head font-semibold" data-testid="community-submit">
          <Send className="h-4 w-4 mr-2" /> {sending ? "Pubblico..." : "Pubblica"}
        </Button>
      </form>

      {posts.length === 0 ? (
        <div className="text-center py-14" data-testid="community-empty">
          <Users className="h-12 w-12 text-orange-200 mx-auto mb-3" />
          <p className="font-head text-lg font-semibold text-slate-700">Ancora nessun messaggio.</p>
          <p className="text-slate-400">Rompi il ghiaccio, scrivi il primo!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <article key={p.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`community-post-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-full ${avatarColor(p.author)} flex items-center justify-center text-white font-head font-bold shrink-0`}>
                  {(p.author || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-bold text-slate-900 truncate">{p.author}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {timeAgo(p.created_at)}</span>
                    {p.spot_name && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.spot_name}</span>}
                  </div>
                </div>
              </div>
              <p className="text-slate-600 mt-2 leading-relaxed whitespace-pre-wrap">{p.message}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
